// Pure helpers shared by StaffPortal and PropertiesGrid (Phase 2 Stage 1).
// No React / no closures — safe to import anywhere.
import { APPEAL_STATUS } from '../constants/statusCodes';

// Parse 'YYYY-MM-DD' as LOCAL midnight (not UTC) to avoid timezone shift.
export function parseLocalDate(v) {
  if (!v) return null;
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

export function fmtMoney(n) {
  if (!n || n <= 0) return '—';
  return '$' + Math.round(n).toLocaleString('en-US');
}

export function fmtDateLocal(d) {
  if (!d) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Priority ladder for the Value Status roll-up (Stage 2 surfaces it as a column).
// Lower rank = higher priority. Steph's ladder.
const APPEAL_STATUS_RANK = {
  [APPEAL_STATUS.appeal_open]: 1,
  [APPEAL_STATUS.under_review]: 2,
  [APPEAL_STATUS.value_pending]: 3,
  [APPEAL_STATUS.withdrawn]: 4,        // was 5, behind 'Appeal Closed' — removed 2026-08-17, zero live rows
  [APPEAL_STATUS.denied]: 5,           // terminal, beside Withdrawn (2026-10-26)
  [APPEAL_STATUS.value_accepted]: 6,
};

// Roll-up stats for one property from its parcels (matched by master_property_id).
// valueByParcelId: Map<parcel_id, { market, taxable }> — prefer-is_primary,
// first-seen-wins (built by the caller from is_primary-DESC-ordered rows).
// ⛔ PRO-RATA (Philip 2026-10-28): splits (utils/prorata splitsForYear) — a parcel split across the client's properties
// is listed under each property it has a share in, and its Market / Taxable count at that share, as in the reports. Its
// own property with no line of its own lists it at 0%. shareById: parcel_id → this property's % (split parcels only).
// Earliest deadline is the earliest UPCOMING (>= today local); past deadlines
// are excluded so the "next thing to act on" surfaces.
export function computePropertyRollup(prop, parcels, valueByParcelId, splits = new Map()) {
  const pp = parcels.filter(par => par.master_property_id === prop.master_property_id
    || (splits.get(par.id)?.has(prop.id) ?? false));
  const shareById = new Map(pp.filter(par => splits.has(par.id)).map(par => [par.id, splits.get(par.id).get(prop.id) ?? 0]));
  const part = (par) => (shareById.has(par.id) ? shareById.get(par.id) / 100 : 1);
  const inAppeal = pp.filter(p => p.appeal_status === APPEAL_STATUS.appeal_open).length;
  // In Flight = not finished. The TERMINAL states are Value Accepted (we never appealed), Withdrawn (we
  // appealed then pulled it) and Denied (we appealed and lost). 'Appeal Closed' used to be one; it is gone.
  const TERMINAL = [APPEAL_STATUS.value_accepted, APPEAL_STATUS.withdrawn, APPEAL_STATUS.denied];
  const inFlight = pp.filter(p => !TERMINAL.includes(p.appeal_status)).length;
  const assessed = pp.reduce((sum, p) => sum + (Number(valueByParcelId.get(p.id)?.market) || 0) * part(p), 0);
  const taxableSum = pp.reduce((sum, p) => sum + (Number(valueByParcelId.get(p.id)?.taxable) || 0) * part(p), 0);
  // valueStatus: highest-priority appeal_status across the property's parcels.
  // ⛔ The status is PER VALUE YEAR (parcel_year_data.appeal_status). The caller merges the selected
  // year's value onto each parcel row before calling this — parcels.appeal_status is RETIRED and must not
  // be read here. Never parcel_appeals either (that table has no status column).
  let valueStatus = 'No Status';
  let bestRank = Infinity;
  for (const p of pp) {
    const rank = APPEAL_STATUS_RANK[p.appeal_status];
    if (rank != null && rank < bestRank) { bestRank = rank; valueStatus = p.appeal_status; }
  }
  const uniqStates = [...new Set(pp.map(p => p.state).filter(Boolean))];
  const state = uniqStates.length === 1 ? uniqStates[0]
              : uniqStates.length > 1  ? `${uniqStates.length} states`
              : (prop.state || '—');
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayMs = today.getTime();
  const futureMs = pp
    .map(p => parseLocalDate(p.appeal_deadline))
    .filter(d => d && d.getTime() >= todayMs)
    .map(d => d.getTime());
  const earliestDeadline = futureMs.length ? new Date(Math.min(...futureMs)) : null;
  return { parcels: pp, count: pp.length, inAppeal, inFlight, assessed, taxableSum, valueStatus, state, earliestDeadline, shareById };
}
